import React, { useState, useEffect } from 'react';
import './Login.css';
import axios from 'axios'; 
import { useNavigate, useLocation } from 'react-router-dom';
import config from '../../config';
import { SsoRedirect } from './ssoRedirect';
import { API_BASE_URL } from '../utils/Constants';
import { decodeAccessToken, getAzureTokenId, createCustomOidToken } from '../utils/tokenUtils';

const BASE_URL = config.ssoRedirect;

const Login = (props) => {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const clientIdValue = queryParams.get('client_id');

    let source; // Declare 'source' outside to be accessible to both 'useEffect' and 'handleSubmit'

    // Security Fix: Clear app storage after logout (but NOT during popup auth)
    // Reference: WSTG-ATHN-06 - Browser Cache Weakness
    useEffect(() => {
        const clearAppStorage = async () => {
            try {
                // CRITICAL: Skip cleanup if this is a popup returning from Azure
                // The popup has window.opener set and/or has auth hash in URL
                const isPopup = window.opener !== null && window.opener !== window;
                const hasAuthHash = window.location.hash.includes('code=') || 
                                   window.location.hash.includes('id_token=') ||
                                   window.location.hash.includes('error=');
                
                if (isPopup || hasAuthHash) {
                    return; // Don't clear anything - let MSAL handle the auth response
                }
                
                // Only clear app-specific keys (NOT MSAL keys in sessionStorage/localStorage)
                // NOTE: accessToken contains CUSTOM token (not Azure token)
                const appKeys = [
                    'directories', 
                    'directoriesListDelete', 
                    'accessToken',  // Custom token (not Azure token)
                    'clientId', 
                    'user.role',
                    'userOid',      // User Object ID
                    'userTid'       // Tenant ID
                ];
                appKeys.forEach(key => localStorage.removeItem(key));
                
                // DON'T clear sessionStorage - MSAL uses it for auth state
                // DON'T clear IndexedDB - MSAL may use it
                // DON'T clear caches during login - only during explicit logout
            } catch (error) {
                // Storage cleanup failed - continue with login
            }
        };
        
        clearAppStorage();
        
        return () => {
            if (source) {
                source.cancel('Component unmounted');
            }
        };
    }, []);
    // End Security Fix

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        source = axios.CancelToken.source(); // Assign 'source' here

        try {
            // /login route is for synthetic journey (client_credentials flow) only
            const url = `${API_BASE_URL}/login`;
            const response = await axios.post(
                `${url}?client_id=${clientIdValue}`,
                {},  
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    cancelToken: source.token // Attach the cancel token to the request
                }
            );
            
            // SECURITY: Do NOT store Azure token in localStorage
            // Create custom token with Azure token's exp and ati, store that instead
            const azureToken = response.data.accessToken;
            const decoded = decodeAccessToken(azureToken);
            
            if (decoded && decoded.oid) {
                // Store non-sensitive identifiers
                localStorage.setItem('clientId', response.data.client_id);
                localStorage.setItem('userOid', decoded.oid);
                if (decoded.tid) {
                    localStorage.setItem('userTid', decoded.tid);
                }
                
                // Create custom token with Azure token expiry and unique ID
                const customToken = createCustomOidToken({
                    oid: decoded.oid,
                    azureTokenExp: decoded.exp,
                    azureTokenId: getAzureTokenId(decoded),
                    isAppFlow: true,
                    clientId: response.data.client_id
                });
                
                // Store CUSTOM token as accessToken (NOT the Azure token)
                localStorage.setItem('accessToken', customToken);
            } else {
                throw new Error('Could not extract oid from Azure token');
            }
            
            navigate("/mdc/auth/media/");
        } catch (error) {
            if (!axios.isCancel(error)) {
                setError('An unexpected error occurred');
            }
        } finally {
            setLoading(false);
        }
    };

    const loginForm = () => (
        <div className="logincontainer">
            <div className="loginform">
                <h1>Login</h1>
                <form onSubmit={handleSubmit}>
                    <button className="loginbtn" type="submit" disabled={loading}>
                        {loading ? 'Logging in...' : 'Login'}
                    </button>
                </form>
                {error && <p className="error">{error}</p>}
            </div>
        </div>
    );

    // Conditional Rendering Logic
    if (clientIdValue) {
        return <div>{loginForm()}</div>;
    }

    if (config.mode === 'sso') {
        return <div>{SsoRedirect(props)}</div>;
    }

    return null;
};

export function logout() {
    window.location.href = `${BASE_URL}/auth/json/sessions/?_action=logout`;
}

export default Login;
